import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
  useReactTable,
} from '@tanstack/react-table'
import classnames from 'classnames'
import { debounce } from 'lodash'
import React, {
  Dispatch,
  forwardRef,
  Ref,
  SetStateAction,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { Table, TableCell, TableHeading, Button, Text } from '@nike/eds'
import { Filter } from "@nike/nike-design-system-icons";
import {
  ColumnFilters,
  exactStringFilter,
  GlobalFilter,
  PaginationControls,
  SortableHeader,
  ColumnFiltersSidePanel,
  ColumnFiltersBadge
} from './table-components'
import { includesStringFilter } from './table-components/ColumnFilters'
import './table.scss'

type ExtendedColumnDef<T> = ColumnDef<T, any> & {
  filterType?: 'text' | 'select' | 'date'
  filterOptions?: Array<{ value: string; label: string }>
  getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>
}

type TableProps<T> = {
  columns: ExtendedColumnDef<T>[]
  data: T[]
  // sorting
  enableSorting?: boolean

  tableTitle?: React.ReactNode
  // sticky elements
  stickyColumnIndex?: number
  disableStickyHeader?: boolean

  // expanded
  enableExpanding?: boolean
  initialExpanded?: ExpandedState

  // pagination
  enablePagination?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  totalCount?: number

  // filtering
  enableGlobalFilter?: boolean
  enableColumnFilters?: boolean
  initialGlobalFilter?: string

  enableColumnFiltersSidePanel?: boolean
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void
  // options
  noDataText?: string
  enableRowSelection?: boolean
  columnOrder?: ColumnOrderState
  globalFilterPlaceholder?: string
  globalFilterLiveSearch?: boolean
  noDataContent?: React.ReactNode
  isNested?: boolean
  className?: string

  // on change events
  onPaginationChange?: (pagination: PaginationState) => void
  onColumnFiltersChange?: (columnFilters: ColumnFiltersState) => void
  onGlobalFilterChange?: (globalFilter: string) => void
  onSortChange?: (sorting: SortingState) => void
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void
  onColumnOrderChange?: (columnOrder: ColumnOrderState) => void

  // other react-table props
  renderExpandedContent?: (row: any) => React.ReactNode;
  [key: string]: any
}

export type TableRef = {
  getTableState: () => TableState
  resetPageIndex: () => void
  setPagination: Dispatch<SetStateAction<PaginationState>>
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>
  setSorting: Dispatch<SetStateAction<SortingState>>
  setExpanded: Dispatch<SetStateAction<ExpandedState>>
  getAllColumns: () => Column<any>[]
}

const hasChildren = <T,>(row: any): row is { children: T[] } => {
  return !!row.children?.length
}

export default forwardRef(function DataTable<T>(
  {
    columns,
    data,
    meta,
    enableSorting = false,
    enablePagination = false,
    enableGlobalFilter = false,
    enableColumnFilters = false,
    enableExpanding = false,
    disableStickyHeader = false,
    enableRowSelection = false,
    enableColumnFiltersSidePanel = false,
    initialColumnFilters = [],
    initialSort = [],
    initialExpanded = {},
    initialGlobalFilter = '',
    pageIndex = 0,
    pageSize = 10,
    pageSizeOptions = [10, 25, 50, 100],
    totalCount = 0,
    autoResetPageIndex = false,
    noDataText = 'No items found.',
    globalFilterPlaceholder = 'Search...',
    globalFilterLiveSearch = true,
    stickyColumnIndex,
    onColumnFiltersChange,
    onGlobalFilterChange,
    onPaginationChange,
    onSortChange,
    onRowSelectionChange,
    onColumnOrderChange,
    onColumnFiltersSidePanelOpenChange,
    renderExpandedContent,
    tableTitle = null,
    noDataContent = null,
    className = '',
    isNested = false,
    ...rest
  }: TableProps<T>,
  ref: Ref<TableRef>
) {
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter)
  const [sorting, setSorting] = useState<SortingState>(initialSort)
  const [expanded, setExpanded] = useState<ExpandedState>(initialExpanded)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex,
    pageSize,
  })
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    rest.state?.columnOrder || []
  )

  const debouncedSetGlobalFilter = useMemo(
    () =>
      debounce((updater: Updater<string>) => {
        setGlobalFilter((prev: string) => {
          const next = updater instanceof Function ? updater(prev) : updater
          onGlobalFilterChange?.(next)
          return next
        })
      }, 100),
    [onGlobalFilterChange]
  )

  const handleGlobalFilterChange = (globalFilter: string) => {
    debouncedSetGlobalFilter(globalFilter)
  }
  const handleSortingChange = (updater: Updater<SortingState>) => {
    setSorting((prev: SortingState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onSortChange?.(next)
      return next
    })
  }

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    setPagination((prev: PaginationState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onPaginationChange?.(next)
      return next
    })
  }

  const handleColumnFiltersChange = (updater: Updater<ColumnFiltersState>) => {
    setColumnFilters((prev: ColumnFiltersState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onColumnFiltersChange?.(next)
      return next
    })
  }

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    setRowSelection((prev: RowSelectionState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onRowSelectionChange?.(next)
      return next
    })
  }

  const handleColumnOrderChange = (updater: Updater<ColumnOrderState>) => {
    setColumnOrder((prev: ColumnOrderState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onColumnOrderChange?.(next)
      return next
    })
  }

  const processedColumns = useMemo(() => {
    return columns.map((column) => {
      if (column.filterType === 'select') {
        return {
          ...column,
          filterFn: column.filterFn || exactStringFilter,
        }
      }

      return {
        ...column,
        filterFn: column.filterFn || includesStringFilter,
      }
    })
  }, [columns])

  const table = useReactTable({
    data,
    columns: processedColumns,
    meta,
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onExpandedChange: rest.onExpandedChange || setExpanded,
    onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
    onColumnOrderChange: handleColumnOrderChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getFilteredRowModel:
      enableGlobalFilter || enableColumnFilters ? getFilteredRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => {
      return hasChildren<T>(row) ? row.children : undefined
    },
    autoResetPageIndex,
    rowCount: totalCount,
    ...rest,
    state: {
      globalFilter: enableGlobalFilter ? globalFilter : undefined,
      sorting: enableSorting ? sorting : [],
      pagination: enablePagination ? pagination : undefined,
      columnFilters: enableColumnFilters || enableColumnFiltersSidePanel? columnFilters : [],
      expanded: enableExpanding ? typeof rest.expanded !== "undefined" ? rest.expanded : expanded : {},
      rowSelection: rowSelection || {},
      columnOrder: columnOrder || [],
      ...rest.state,
    },
  })
  // expose table state getter and reset PageIndex for consumers
  useImperativeHandle(ref, () => ({
    getTableState: () => table.getState(),
    resetPageIndex: () =>
      setPagination({
        ...pagination,
        pageIndex: 0,
      }),
    setPagination,
    setColumnFilters,
    setSorting,
    setExpanded,
    getAllColumns() {
      return table.getAllColumns();
    },
  }))

  const rows = table.getRowModel().rows
  const showRowHover = enableRowSelection;
  
  const tableState = table.getState();

  return (
    <div className="tableWrapper">

      {enableColumnFiltersSidePanel && (
        <ColumnFiltersSidePanel
          isOpen={rest.state?.columnFiltersSidePanelIsOpen}
          table={table}
          columns={table.getAllColumns() as any || []}
          columnFilters={tableState?.columnFilters}
          handleColumnFiltersChange={handleColumnFiltersChange}
          onClose={() => onColumnFiltersSidePanelOpenChange?.(false)}
        />
      )}
      {enableGlobalFilter || enableColumnFiltersSidePanel || tableTitle ? (
        <div className="eds-flex eds-flex--m-justify-content-flex-end eds-flex--align-items-center eds-gap--32 eds-spacing--mb-16" >
          <div className="eds-flex--grow-1">
            {tableTitle}
          </div>
          {enableGlobalFilter && (
            <div className='globalFilterContainer'>
            <GlobalFilter
              globalFilter={globalFilter}
              handleGlobalFilterChange={handleGlobalFilterChange}
              placeholder={globalFilterPlaceholder}
              liveSearch={globalFilterLiveSearch}
            />
            </div>
          )}
          {enableColumnFiltersSidePanel && (
            <div style={{ position: "relative" }}>
              <Button
                beforeSlot={<Filter />}
                size='small'
                onClick={() => onColumnFiltersSidePanelOpenChange?.(true)}
                style={{ position: "relative" }}
              >
                Filters
              </Button>
              <ColumnFiltersBadge
                count={tableState?.columnFilters?.length}
              />
            </div>
          )}
        </div>) : null}
      <div
        className={classnames(
          'tableContainer',
          'overflowAuto',
          { 'rowHover': showRowHover },
          { 'stickyHeaderDisabled': disableStickyHeader },
          { 'hasPagination': enablePagination}
        )}
         style={rest.style}
      >
        <Table className={classnames(className, {'isNested': isNested})} isFullWidth>
          <thead className='tableHeader'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <TableHeading
                    className={classnames(`table--header__${header.id}`, {
                      stickyColumnHeader: index === stickyColumnIndex,
                    })}
                    style={{
                      minWidth: header.getSize(),
                    }}
                    key={`${header.id}.${header.column.columnDef.header}`}
                  >
                    <div className='headerCellLabel'>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {enableSorting && (
                        <SortableHeader
                          getCanSort={header.column.getCanSort}
                          // @ts-ignore
                          getToggleSortingHandler={header.column.getToggleSortingHandler}
                          getIsSorted={header.column.getIsSorted}
                        />
                      )}
                    </div>

                    {enableColumnFilters && header.column.getCanFilter() ? (
                      <ColumnFilters
                        enableColumnFilters={enableColumnFilters}
                        header={header as any}
                      />
                    ) : null}
                  </TableHeading>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row) => {
                return (
                  <React.Fragment key={row.id}>
                  <tr
                    className={row.getIsSelected() ? 'selected' : undefined}
                    onClick={e => {
                      // add if back in to disable deselection
                      // if (!row.getIsSelected()) {
                        row.getToggleSelectedHandler()(e);
                      // }
                    }}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        className={classnames({
                          'stickyColumnBody': index === stickyColumnIndex,
                          'expandableRowCell': row.getCanExpand(),
                        })}
                        key={`${cell.id}.${cell.column.columnDef.header}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                  <tr
                    className='expandedRow'
                    key={`${row.id}-expanded`}
                  >
                    <td colSpan={row.getAllCells().length}>
                      <div className={'expandedContentWrapper'}> 
                      {typeof renderExpandedContent === 'function'
                        ? renderExpandedContent(row.original)
                        : <div></div>}
                      </div>
                    </td>
                  </tr>
                  )}
                  </React.Fragment>
                )
              })
            ) : (
              <tr>
                <TableCell colSpan={table.getAllColumns().length}>
                  {noDataContent || (
                    <div className='noDataText'>
                      <Text font="title-4" as="h4" className="eds-spacing--mt-56 eds-spacing--mb-56" >{noDataText}</Text>
                    </div>
                  )}
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {enablePagination && <PaginationControls table={table} pageSizeOptions={pageSizeOptions} totalCount={totalCount} />}
    </div>
  )
})
